import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type ClassNames, type DateRange, DayPicker } from 'react-day-picker';
import moment from 'moment';

import { cn } from 'src/lib/utils';
import { buttonVariants } from 'src/components/ui/button';

// A date range as our forms hold it: '' before a pick, and possibly seeded with date strings.
export type DateRangeValue = { from?: Date | string; to?: Date | string };

const toDate = (value?: Date | string) => (value ? new Date(value) : undefined);

const CalendarChevron = ({ orientation }: { orientation?: 'up' | 'down' | 'left' | 'right' }) =>
  orientation === 'left' ? (
    <ChevronLeft className="h-4 w-4" />
  ) : (
    <ChevronRight className="h-4 w-4" />
  );

// DayPicker reads Dates; empty values stay empty so it falls back to its defaults.
export const toDateRange = (range?: DateRangeValue): DateRange | undefined =>
  range && { from: toDate(range.from), to: toDate(range.to) };

const DEFAULT_CLASS_NAMES: Partial<ClassNames> = {
  months: 'flex flex-col laptop:flex-row space-y-4 laptop:space-x-4 laptop:space-y-0',
  month: 'space-y-4',
  month_caption: 'flex justify-center pt-1 relative items-center',
  caption_label: 'text-sm font-medium',
  nav: 'space-x-1 flex items-center',
  button_previous: cn(
    buttonVariants({ variant: 'outline' }),
    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1',
  ),
  button_next: cn(
    buttonVariants({ variant: 'outline' }),
    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1',
  ),
  month_grid: 'w-full border-collapse space-y-1',
  weekdays: 'flex',
  weekday: 'text-muted-foreground rounded-sm-5 w-9 font-normal text-[0.8rem]',
  week: 'flex w-full mt-2',
  day: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].range_end)]:rounded-r-[5px] [&:has([aria-selected].outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
  day_button: cn(
    buttonVariants({ variant: 'ghost' }),
    'h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-green_hover',
  ),
  range_end: 'day-range-end',
  selected:
    'bg-green-default text-ebony hover:bg-green_hover hover:text-ebony focus:bg-green-default focus:text-ebony',
  today: 'bg-green_hover text-accent-foreground',
  outside:
    'day-outside text-muted-foreground opacity-50 aria-selected:bg-green_hover/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
  disabled: 'text-muted-foreground opacity-50',
  range_middle: 'aria-selected:bg-green_hover aria-selected:text-accent-foreground',
  hidden: 'invisible',
};

// The compact purple range picker shared by the conversation, analytics and payment filters.
const FILTER_CLASS_NAMES: Partial<ClassNames> = {
  months: 'relative flex flex-col gap-2.5 sm:flex-row',
  month: 'flex-1 rounded-sm-5 bg-light p-3',
  month_caption: 'flex h-6 items-center justify-center',
  caption_label: 'text-xs font-bold text-ebony',
  nav: 'absolute inset-x-0 top-3 flex justify-between px-3',
  button_previous: 'size-6 text-grey_450 hover:text-ebony disabled:opacity-50',
  button_next: 'size-6 text-grey_450 hover:text-ebony disabled:opacity-50',
  month_grid: 'mt-2 w-full border-collapse',
  weekdays: 'flex justify-between',
  weekday: 'size-7 text-xs font-semibold text-ebony',
  week: 'mt-1 flex justify-between',
  day: 'size-7 p-0 text-center text-xs',
  day_button: cn(
    buttonVariants({ variant: 'ghost' }),
    'size-7 rounded-[1px] p-0 text-xs font-medium text-ebony_opacity_70 hover:bg-purple_light aria-selected:opacity-100',
  ),
  selected: 'bg-purple_opacity_20 text-ebony',
  range_start: '!bg-purple',
  range_end: '!bg-purple',
  range_middle: 'aria-selected:bg-purple_opacity_20',
  today: 'font-bold',
  outside: 'opacity-30',
  disabled: 'opacity-30',
  hidden: 'invisible',
};

function Calendar({
  className = undefined,
  classNames = undefined,
  showOutsideDays = true,
  variant = 'default',
  ...props
}: React.ComponentProps<typeof DayPicker> & { variant?: 'default' | 'filter' }) {
  const isFilter = variant === 'filter';

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(isFilter ? 'p-0' : 'p-3', className)}
      classNames={{
        ...(isFilter ? FILTER_CLASS_NAMES : DEFAULT_CLASS_NAMES),
        ...classNames,
      }}
      {...(isFilter && {
        weekStartsOn: 1,
        formatters: { formatWeekdayName: (day: Date) => moment(day).format('dd').charAt(0) },
      })}
      components={{ Chevron: CalendarChevron }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
